import {
  COMPARE_BY_OPTION,
  type CompareByOption,
} from "@/run-detail/compare-by"

import type { MetricViews } from "@/types/MetricViewConfig"
import type { MetricLayoutConfig } from "@/utils/metricLayout"

import {
  DEFAULT_REPORT_PANE_VISIBILITY,
  resolveReportPaneVisibility,
  type CustomDimensionsConfig,
  type ReportPaneVisibility,
  type SavedReportViewConfig,
} from "./reportViewConfig"
import { DEFAULT_REPORT_VIEW_MODE, type ReportViewMode } from "./reportViewMode"

const EMPTY_METRIC_LAYOUT: MetricLayoutConfig = { sections: [] }

export const EMPTY_CUSTOM_DIMENSIONS_CONFIG: CustomDimensionsConfig = {
  dimensions: [],
}

const REPORT_ROUTE_ID_LENGTH = 26

export interface ReportCompareByConfigState {
  compareBy: CompareByOption
  metadataKey: string | null
  customDimensionId: string | null
  viewMode: ReportViewMode
  secondaryCompareBy: CompareByOption
  secondaryMetadataKey: string | null
  secondaryCustomDimensionId: string | null
  metricLayout: MetricLayoutConfig
  paneVisibility: ReportPaneVisibility
}

export function getReportRouteId(
  value: string | string[] | undefined,
  isReady: boolean,
): string | null {
  if (!isReady) {
    return null
  }

  return typeof value === "string" && value.length === REPORT_ROUTE_ID_LENGTH
    ? value
    : null
}

export function getDefaultReportCompareByConfig(): ReportCompareByConfigState {
  return {
    compareBy: COMPARE_BY_OPTION.NONE,
    metadataKey: null,
    customDimensionId: null,
    viewMode: DEFAULT_REPORT_VIEW_MODE,
    secondaryCompareBy: COMPARE_BY_OPTION.NONE,
    secondaryMetadataKey: null,
    secondaryCustomDimensionId: null,
    metricLayout: EMPTY_METRIC_LAYOUT,
    paneVisibility: DEFAULT_REPORT_PANE_VISIBILITY,
  }
}

export function getSavedReportCompareByConfig(
  savedReportViewConfig: SavedReportViewConfig | null,
): {
  compareByColorOverrides: Record<string, string>
  compareByConfig: ReportCompareByConfigState
} | null {
  if (!savedReportViewConfig) {
    return null
  }

  return {
    compareByColorOverrides:
      savedReportViewConfig.compareByColorOverrides ?? {},
    compareByConfig: {
      compareBy: savedReportViewConfig.compareBy ?? COMPARE_BY_OPTION.NONE,
      metadataKey: savedReportViewConfig.metadataKey ?? null,
      customDimensionId: savedReportViewConfig.customDimensionId ?? null,
      viewMode: savedReportViewConfig.viewMode ?? DEFAULT_REPORT_VIEW_MODE,
      secondaryCompareBy:
        savedReportViewConfig.secondaryCompareBy ?? COMPARE_BY_OPTION.NONE,
      secondaryMetadataKey: savedReportViewConfig.secondaryMetadataKey ?? null,
      secondaryCustomDimensionId:
        savedReportViewConfig.secondaryCustomDimensionId ?? null,
      metricLayout: savedReportViewConfig.metricLayout ?? EMPTY_METRIC_LAYOUT,
      paneVisibility: resolveReportPaneVisibility(
        savedReportViewConfig.paneVisibility,
      ),
    },
  }
}

export function hasReportConfigChanges({
  compareByColorOverrides,
  compareByConfig,
  customDimensions,
  metricViews,
  savedMetricConfig,
  savedReportViewConfig,
}: {
  compareByColorOverrides: Record<string, string>
  compareByConfig: ReportCompareByConfigState
  customDimensions: CustomDimensionsConfig
  metricViews: MetricViews
  savedMetricConfig?: MetricViews | null
  savedReportViewConfig: SavedReportViewConfig | null
}): boolean {
  const hasMetricConfigChanges =
    JSON.stringify(metricViews) !== JSON.stringify(savedMetricConfig ?? {})

  const savedCustomDimensions =
    savedReportViewConfig?.customDimensions ?? EMPTY_CUSTOM_DIMENSIONS_CONFIG
  const hasCustomDimensionsChanges =
    JSON.stringify(customDimensions) !== JSON.stringify(savedCustomDimensions)

  if (!savedReportViewConfig) {
    return hasMetricConfigChanges || hasCustomDimensionsChanges
  }

  const hasViewConfigChanges =
    compareByConfig.compareBy !==
      (savedReportViewConfig.compareBy ?? COMPARE_BY_OPTION.NONE) ||
    compareByConfig.metadataKey !==
      (savedReportViewConfig.metadataKey ?? null) ||
    compareByConfig.customDimensionId !==
      (savedReportViewConfig.customDimensionId ?? null) ||
    compareByConfig.viewMode !==
      (savedReportViewConfig.viewMode ?? DEFAULT_REPORT_VIEW_MODE) ||
    compareByConfig.secondaryCompareBy !==
      (savedReportViewConfig.secondaryCompareBy ?? COMPARE_BY_OPTION.NONE) ||
    compareByConfig.secondaryMetadataKey !==
      (savedReportViewConfig.secondaryMetadataKey ?? null) ||
    compareByConfig.secondaryCustomDimensionId !==
      (savedReportViewConfig.secondaryCustomDimensionId ?? null) ||
    JSON.stringify(compareByConfig.metricLayout) !==
      JSON.stringify(
        savedReportViewConfig.metricLayout ?? EMPTY_METRIC_LAYOUT,
      ) ||
    JSON.stringify(compareByColorOverrides) !==
      JSON.stringify(savedReportViewConfig.compareByColorOverrides ?? {}) ||
    JSON.stringify(
      resolveReportPaneVisibility(compareByConfig.paneVisibility),
    ) !==
      JSON.stringify(
        resolveReportPaneVisibility(savedReportViewConfig.paneVisibility),
      )

  return (
    hasViewConfigChanges || hasMetricConfigChanges || hasCustomDimensionsChanges
  )
}
